/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument */
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import {
  Copy,
  Check,
  Play,
  ChevronUp,
  ChevronDown,
  Lock,
  Unlock,
  X,
  Hash,
  GitBranch,
  ChevronDown as CollapseIcon,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Zap,
  Terminal,
  Wifi,
  BarChart2,
  Database,
  List,
  ChevronRight,
  ArrowLeft,
  Share2,
  FlaskConical,
} from 'lucide-react';

import type { StoredCaptureEvent } from '../../lib/types';
import { cn } from '../../lib/utils';

import { InspectorDataProvider, useExecutionData } from './inspector-context';
import { TimelineRail } from './timeline-rail';
import { ConsolePanel } from './console-panel';
import { NetworkPanel } from './network-panel';
import { PerformancePanel } from './performance-panel';
import { StatePanel } from './state-panel';
import { EventsPanel } from './events-panel';
import { DetailDrawer } from './detail-drawer';
import { FlowPanel } from './flow-panel';
import { ReplayDialog } from './replay-dialog';
import { INSPECTOR_CAPABILITY_COLORS } from './inspector-capability-colors';

// ─── Constants ─────────────────────────────────────────────────────────────────

const CAPABILITY_COLORS = INSPECTOR_CAPABILITY_COLORS;

// ─── Helpers ───────────────────────────────────────────────────────────────────

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

function shortEntry(entry: string): string {
  return entry
    .replace(/\.js$|\.ts$/, '')
    .split(/[/\\]/)
    .slice(-2)
    .join('/');
}

/** Parse a timestamp safely — returns a Date or null */
function safeDate(value: string | number | undefined | null): Date | null {
  if (value == null) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

function formatTime(value: string | number | undefined | null): string {
  const d = safeDate(value);
  if (!d) return '—';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function isProtected(payload: unknown): boolean {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    (payload as Record<string, unknown>)['__protected'] === true
  );
}

function isEncryptedEnvelope(payload: unknown): payload is Record<string, unknown> {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    (payload as Record<string, unknown>)['__enc'] === 'asymmetric'
  );
}

async function decryptEnvelopeViaAgent(
  agentUrl: string,
  payload: Record<string, unknown>,
): Promise<unknown> {
  const res = await fetch(agentUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'decrypt', type: 'decrypt', payload }),
  });

  if (!res.ok) {
    throw new Error(`Agent returned ${res.status}`);
  }

  const json = (await res.json()) as { ok?: boolean; data?: unknown; error?: string };
  if (json?.ok !== true) {
    throw new Error(json?.error ?? 'Agent decrypt failed');
  }
  return json.data;
}

async function decryptValueViaAgent(agentUrl: string, value: unknown): Promise<unknown> {
  if (isEncryptedEnvelope(value)) {
    return decryptEnvelopeViaAgent(agentUrl, value);
  }

  if (Array.isArray(value)) {
    return Promise.all(value.map((item): Promise<unknown> => decryptValueViaAgent(agentUrl, item)));
  }

  if (value != null && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj)) {
      out[k] = await decryptValueViaAgent(agentUrl, v);
    }
    return out;
  }

  return value;
}

async function decryptEventsViaAgent(
  agentUrl: string,
  events: StoredCaptureEvent[],
): Promise<StoredCaptureEvent[]> {
  return Promise.all(
    events.map(async (event) => {
      const payload = await decryptValueViaAgent(agentUrl, event.payload);
      const requestPayload =
        event.requestPayload === undefined
          ? undefined
          : await decryptValueViaAgent(agentUrl, event.requestPayload);
      const responsePayload =
        event.responsePayload === undefined
          ? undefined
          : await decryptValueViaAgent(agentUrl, event.responsePayload);
      const errorPayload =
        event.errorPayload === undefined
          ? undefined
          : await decryptValueViaAgent(agentUrl, event.errorPayload);
      return {
        ...event,
        payload,
        requestPayload,
        responsePayload,
        errorPayload,
      };
    }),
  );
}

// ─── Copy button ───────────────────────────────────────────────────────────────

function CopyButton({
  text,
  label,
}: {
  text: string;
  label?: string;
}): import('react/jsx-runtime').JSX.Element {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={(): void => {
        void navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout((): void => setCopied(false), 2000);
      }}
      className="flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-white/[0.04] border border-white/8 hover:bg-white/[0.08] transition-all text-[10px] font-mono text-muted-foreground/60 hover:text-foreground"
    >
      <code>{label ?? text.slice(0, 14)}</code>
      {copied ? (
        <Check className="h-2.5 w-2.5 text-[var(--flux-success)]" />
      ) : (
        <Copy className="h-2.5 w-2.5" />
      )}
    </button>
  );
}

// ─── Narrative summary bar ─────────────────────────────────────────────────────

function NarrativeBar(): import('react/jsx-runtime').JSX.Element {
  const { events, status, durationMs, bootEvent, errorEvent } = useExecutionData();
  const isError = status === 'error';
  // moved to context
  // moved to context
  const fetchEvents = events.filter((e): boolean => e.capability === 'fetch');
  const slowFetch = fetchEvents.find((e): boolean => (e.durationMs ?? 0) > 500);

  const errPayload = errorEvent?.payload as Record<string, unknown> | null;
  const errMsg = errPayload?.['message'] ?? errPayload?.['error'] ?? errPayload?.['msg'] ?? null;
  const entry =
    bootEvent?.payload != null && typeof bootEvent.payload === 'object'
      ? ((): string => {
          const p = bootEvent.payload as Record<string, unknown>;
          return (
            (p['entry'] as string | undefined) ?? (p['name'] as string | undefined) ?? 'unknown'
          );
        })()
      : 'unknown';

  if (isError) {
    // Determine root cause confidence
    let rootCause = 'Internal function error';
    let confidence = 'Low';
    if (errMsg != null && (errMsg as string).toLowerCase().includes('timeout')) {
      rootCause = 'Execution timed out';
      confidence = 'High';
      if (slowFetch) {
        rootCause = 'External dependency timeout (fetch)';
      }
    } else if (errorEvent?.capability === 'fetch') {
      rootCause = 'External API failure';
      confidence = 'High';
    } else if (errMsg) {
      rootCause = 'Unhandled exception';
      confidence = 'Medium';
    }

    return (
      <div className="shrink-0 flex flex-col gap-4 px-6 py-6 border-b border-[var(--flux-error)]/20 bg-[var(--flux-error)]/[0.03]">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-xl bg-[var(--flux-error)]/10 text-[var(--flux-error)]">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <div className="flex flex-col gap-1.5 flex-1 min-w-0">
            <h2 className="text-xl font-bold text-[var(--flux-error)] truncate">
              {errMsg != null ? (errMsg as string) : 'Execution Failed'}
            </h2>
            <p className="text-muted-foreground font-mono text-sm border-l-2 border-[var(--flux-error)]/30 pl-3 ml-1 mt-1">
              <span className="text-foreground/80 font-semibold">{shortEntry(entry)}</span> failed
              after <span className="text-[var(--flux-error)]/80">{durationMs}ms</span>
            </p>
          </div>
        </div>
        <div className="pl-[60px]">
          <div className="inline-flex items-center gap-4 bg-[#0a0c11]/80 backdrop-blur border border-white/5 rounded-lg px-4 py-2.5">
            <div className="flex flex-col">
              <span className="text-[10px] uppercase font-mono text-muted-foreground/40 font-bold mb-0.5">
                Root Cause
              </span>
              <span className="text-sm font-medium text-foreground/80">{rootCause}</span>
            </div>
            <div className="w-px h-8 bg-white/10" />
            <div className="flex flex-col">
              <span className="text-[10px] uppercase font-mono text-muted-foreground/40 font-bold mb-0.5">
                Confidence
              </span>
              <span
                className={cn(
                  'text-sm font-mono font-bold',
                  confidence === 'High'
                    ? 'text-blue-400'
                    : confidence === 'Medium'
                      ? 'text-[var(--flux-warning)]'
                      : 'text-muted-foreground/50',
                )}
              >
                {confidence}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state: clean 1-liner
  const slowFetchUrl =
    slowFetch?.payload != null && typeof slowFetch.payload === 'object'
      ? ((): string => {
          const p = slowFetch.payload as Record<string, unknown>;
          const url =
            (p['0'] as string | undefined) ??
            (p['url'] as string | undefined) ??
            (p['input'] as string | undefined) ??
            '';
          try {
            return new URL(url).hostname;
          } catch {
            return url.slice(0, 30);
          }
        })()
      : null;

  return (
    <div
      className={cn(
        'shrink-0 flex items-center gap-3 px-5 py-2.5 border-b text-[12px] font-mono',
        slowFetch
          ? 'bg-[var(--flux-warning)]/[0.04] border-[var(--flux-warning)]/15'
          : 'bg-[var(--flux-success)]/[0.04] border-[var(--flux-success)]/10',
      )}
    >
      {slowFetch ? (
        <Clock className="h-3.5 w-3.5 text-[var(--flux-warning)] shrink-0" />
      ) : (
        <CheckCircle2 className="h-3.5 w-3.5 text-[var(--flux-success)] shrink-0" />
      )}
      <span className="flex items-center gap-2.5 flex-wrap">
        <span className="text-foreground/90 font-bold">{shortEntry(entry)}</span>
        {fetchEvents.length > 0 && (
          <>
            <span className="text-muted-foreground/30">→</span>
            <span className="text-blue-400/80">
              {(fetchEvents.length / 2) | 0} fetch call{fetchEvents.length / 2 > 1 ? 's' : ''}
            </span>
          </>
        )}
        <span className="text-muted-foreground/30">→</span>
        <span className="text-[var(--flux-success)]">succeeded</span>
      </span>
      {slowFetchUrl && (
        <span className="ml-auto text-[var(--flux-warning)]/80 shrink-0 bg-[var(--flux-warning)]/10 px-2 py-0.5 rounded">
          slow fetch: {slowFetchUrl} ({slowFetch?.durationMs}ms)
        </span>
      )}
    </div>
  );
}

// ─── Insight engine ─────────────────────────────────────────────────────────────

function deriveInsights(
  events: import('./inspector-context').ProcessedEvent[],
  status: string,
  durationMs: number,
  errorEvent?: import('./inspector-context').ProcessedEvent,
): { type: 'error' | 'warn' | 'info' | 'success'; text: string }[] {
  const insights: { type: 'error' | 'warn' | 'info' | 'success'; text: string }[] = [];
  const fetchEvents = events.filter((e): boolean => e.capability === 'fetch');
  const slowFetch = fetchEvents.find((e): boolean => (e.durationMs ?? 0) > 500);

  if (status === 'error') {
    const errPayload = errorEvent?.phase === 'span' ? errorEvent.errorPayload : errorEvent?.payload;
    const errMsg =
      typeof errPayload === 'object' && errPayload !== null
        ? ((errPayload as Record<string, unknown>)['message'] ??
          (errPayload as Record<string, unknown>)['error'] ??
          null)
        : null;
    insights.push({
      type: 'error',
      text:
        errMsg != null
          ? `Error: ${(errMsg as string).slice(0, 120)}`
          : 'Execution ended with an error',
    });
  } else {
    insights.push({ type: 'success', text: 'Executed successfully' });
  }

  if (slowFetch) {
    const url =
      typeof slowFetch.payload === 'object' && slowFetch.payload !== null
        ? String(
            ((slowFetch.payload as Record<string, unknown>)['url'] ??
              (slowFetch.payload as Record<string, unknown>)['0'] ??
              '') as string,
          ).slice(0, 60)
        : '';
    insights.push({
      type: 'warn',
      text: `Slow outbound fetch${url ? ` to ${url}` : ''} (${slowFetch.durationMs}ms)`,
    });
  }
  if (durationMs > 1500)
    insights.push({ type: 'warn', text: `High total duration: ${formatDuration(durationMs)}` });
  const consoleErrors = events.filter(
    (e): boolean => e.capability === 'console' && e.action === 'error',
  );
  if (consoleErrors.length > 0)
    insights.push({
      type: 'warn',
      text: `${consoleErrors.length} console error${consoleErrors.length > 1 ? 's' : ''} emitted`,
    });
  return insights;
}

function InsightRow({
  type,
  text,
}: {
  type: 'error' | 'warn' | 'info' | 'success';
  text: string;
}): import('react/jsx-runtime').JSX.Element {
  const cfg = {
    error: {
      cls: 'bg-[var(--flux-error)]/[0.06] border-[var(--flux-error)]/20 text-[var(--flux-error)]',
      icon: <AlertTriangle className="h-3 w-3 shrink-0" />,
    },
    warn: {
      cls: 'bg-[var(--flux-warning)]/[0.06] border-[var(--flux-warning)]/20 text-[var(--flux-warning)]',
      icon: <AlertTriangle className="h-3 w-3 shrink-0" />,
    },
    info: {
      cls: 'bg-blue-500/[0.06] border-blue-500/20 text-blue-400',
      icon: <Zap className="h-3 w-3 shrink-0" />,
    },
    success: {
      cls: 'bg-[var(--flux-success)]/[0.04] border-[var(--flux-success)]/15 text-[var(--flux-success)]',
      icon: <CheckCircle2 className="h-3 w-3 shrink-0" />,
    },
  }[type];
  return (
    <div
      className={cn(
        'flex items-center gap-2.5 px-3.5 py-2 rounded-lg border text-[11px] font-mono',
        cfg.cls,
      )}
    >
      {cfg.icon}
      <span className="truncate">{text}</span>
    </div>
  );
}

function StatChip({
  label,
  value,
  cls,
}: {
  label: string;
  value: string;
  cls?: string | undefined;
}): import('react/jsx-runtime').JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[9px] font-mono text-muted-foreground/30 uppercase tracking-widest">
        {label}
      </span>
      <span className={cn('text-sm font-semibold font-mono', cls ?? 'text-foreground')}>
        {value}
      </span>
    </div>
  );
}

// ─── Tab definitions ───────────────────────────────────────────────────────────

const MIN_DOCK_H = 36; // collapsed — just the tab bar
const DEFAULT_DOCK_H = 280;
const MAX_DOCK_RATIO = 0.85; // max 85% of viewport height

type Panel = 'console' | 'network' | 'performance' | 'state' | 'events';
const TABS: { id: Panel; label: string; icon: React.ReactNode }[] = [
  { id: 'console', label: 'Console', icon: <Terminal className="h-3 w-3" /> },
  { id: 'network', label: 'Network', icon: <Wifi className="h-3 w-3" /> },
  { id: 'performance', label: 'Performance', icon: <BarChart2 className="h-3 w-3" /> },
  { id: 'state', label: 'State', icon: <Database className="h-3 w-3" /> },
  { id: 'events', label: 'Events', icon: <List className="h-3 w-3" /> },
];

// ─── Main component ─────────────────────────────────────────────────────────────

interface ExecutionInspectorProps {
  executionId: string;
  projectId: string;
  events: StoredCaptureEvent[];
  agentUrl?: string | null | undefined;
  /**
   * Static embed (e.g. marketing): no storage, no decrypt, no replay / pin / share.
   */
  readOnlyStatic?: boolean | undefined;
  /**
   * When `readOnlyStatic` and `fill`, the outer shell uses full available height
   * (e.g. marketing `/demo` full viewport) instead of a bounded card.
   */
  readOnlyStaticLayout?: 'default' | 'fill' | undefined;
}

export function ExecutionInspector({
  executionId,
  projectId,
  events: rawEvents,
  agentUrl = null,
  readOnlyStatic = false,
  readOnlyStaticLayout = 'default',
}: ExecutionInspectorProps): import('react/jsx-runtime').JSX.Element {
  const localAgentKey = useMemo((): string => `flux_local_agent_url_${projectId}`, [projectId]);
  const unlockStorageKey = useMemo(
    (): string => `flux:execution-data-unlocked:${projectId}`,
    [projectId],
  );
  const [localAgentUrl, setLocalAgentUrl] = useState<string | null>(null);
  const [isDataUnlocked, setIsDataUnlocked] = useState(true);
  const [decrypting, setDecrypting] = useState(false);
  const [decryptError, setDecryptError] = useState<string | null>(null);
  const [displayEvents, setDisplayEvents] = useState<StoredCaptureEvent[]>(rawEvents);

  useEffect((): void => {
    if (readOnlyStatic) return;
    if (typeof window === 'undefined') return;
    const storedLocal = localStorage.getItem(localAgentKey);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLocalAgentUrl(storedLocal ? storedLocal.trim() : null);

    const storedUnlock = sessionStorage.getItem(unlockStorageKey);
    // Default is unlocked when no value is stored.
    setIsDataUnlocked(storedUnlock !== '0');
  }, [readOnlyStatic, localAgentKey, unlockStorageKey]);

  useEffect(() => {
    if (readOnlyStatic) return;
    const onStorage = (event: StorageEvent): void => {
      if (event.key === localAgentKey) {
        setLocalAgentUrl(event.newValue ? event.newValue.trim() : null);
      }
    };
    window.addEventListener('storage', onStorage);
    return (): void => window.removeEventListener('storage', onStorage);
  }, [readOnlyStatic, localAgentKey]);

  const resolvedAgentUrl = useMemo((): string | null => {
    if (readOnlyStatic) return null;
    const local = localAgentUrl?.trim();
    if (local) return local;
    const project = agentUrl?.trim();
    return project ?? null;
  }, [readOnlyStatic, localAgentUrl, agentUrl]);

  useEffect((): void => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDisplayEvents(rawEvents);
  }, [rawEvents]);

  useEffect(() => {
    if (readOnlyStatic) {
      return;
    }
    let cancelled = false;

    if (!isDataUnlocked) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDisplayEvents(rawEvents);
      setDecrypting(false);
      setDecryptError(null);
      return;
    }

    if (!resolvedAgentUrl) {
      setDisplayEvents(rawEvents);
      setDecrypting(false);
      setDecryptError('No agent URL configured for decrypt mode.');
      return;
    }

    setDecrypting(true);
    setDecryptError(null);

    decryptEventsViaAgent(resolvedAgentUrl, rawEvents)
      .then((decrypted): void => {
        if (!cancelled) setDisplayEvents(decrypted);
      })
      .catch((err): void => {
        if (cancelled) return;
        setDisplayEvents(rawEvents);
        setDecryptError(err instanceof Error ? err.message : 'Failed to decrypt via agent');
      })
      .finally((): void => {
        if (!cancelled) setDecrypting(false);
      });

    return (): void => {
      cancelled = true;
    };
  }, [readOnlyStatic, isDataUnlocked, resolvedAgentUrl, rawEvents]);

  const toggleDataLock = useCallback((): void => {
    setIsDataUnlocked((prev): boolean => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        // 1 = unlocked, 0 = locked (raw data mode)
        if (next) sessionStorage.setItem(unlockStorageKey, '1');
        else sessionStorage.setItem(unlockStorageKey, '0');
      }
      return next;
    });
  }, [unlockStorageKey]);

  return (
    <InspectorDataProvider
      executionId={executionId}
      projectId={projectId}
      agentUrl={resolvedAgentUrl}
      rawEvents={displayEvents}
    >
      <ExecutionInspectorInner
        isDataUnlocked={isDataUnlocked}
        decrypting={decrypting}
        decryptError={decryptError}
        canDecrypt={!!resolvedAgentUrl}
        onToggleDataLock={toggleDataLock}
        readOnlyStatic={readOnlyStatic}
        readOnlyStaticLayout={readOnlyStaticLayout}
      />
    </InspectorDataProvider>
  );
}

function ExecutionInspectorInner({
  isDataUnlocked,
  decrypting,
  decryptError,
  canDecrypt,
  onToggleDataLock,
  readOnlyStatic,
  readOnlyStaticLayout,
}: {
  isDataUnlocked: boolean;
  decrypting: boolean;
  decryptError: string | null;
  canDecrypt: boolean;
  onToggleDataLock: () => void;
  readOnlyStatic: boolean;
  readOnlyStaticLayout: 'default' | 'fill';
}): import('react/jsx-runtime').JSX.Element {
  const {
    executionId,
    projectId,
    agentUrl,
    events,
    durationMs,
    startAt,
    status,
    networkCalls,
    bootEvent,
    errorEvent,
  } = useExecutionData();
  const isError = status === 'error';
  const [searchParams] = useSearchParams();
  const [activePanel, setActivePanel] = useState<Panel>('console');
  // Replaced by useExecutionData

  const [selectedEventIdx, setSelectedEventIdx] = useState<number | null>(
    events.length > 0 ? 0 : null,
  );
  const [drawerOpen, setDrawerOpen] = useState(events.length > 0);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [dockHeight, setDockHeight] = useState(DEFAULT_DOCK_H);
  const [replayOpen, setReplayOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [pinning, setPinning] = useState(false);
  const [pinError, setPinError] = useState<string | null>(null);

  // Check on mount whether this execution is already saved as a test case.
  useEffect((): void => {
    if (readOnlyStatic) return;
    if (!executionId || !projectId) return;
    fetch(`/api/projects/${projectId}/test-cases?executionId=${encodeURIComponent(executionId)}`)
      .then((r): Promise<unknown> | null => (r.ok ? (r.json() as Promise<unknown>) : null))
      .then((data): void => {
        if (
          Array.isArray(data) &&
          data.some(
            (tc): boolean =>
              typeof tc === 'object' &&
              tc !== null &&
              (tc as { execution_id?: string }).execution_id === executionId,
          )
        ) {
          setIsPinned(true);
        }
      })
      .catch((): void => {});
  }, [readOnlyStatic, executionId, projectId]);

  const dragState = useRef<{ startY: number; startH: number } | null>(null);
  const isCollapsed = dockHeight <= MIN_DOCK_H;

  function makeDragger(
    ref: React.MutableRefObject<{ startY: number; startH: number } | null>,
    setter: (fn: (h: number) => number) => void,
    minH: number,
    maxRatio: number,
  ) {
    return (e: React.MouseEvent): void => {
      e.preventDefault();
      ref.current = { startY: e.clientY, startH: 0 };
      // capture current height immediately
      setter((h): number => {
        if (ref.current) ref.current.startH = h;
        return h;
      });
      const onMove = (ev: MouseEvent): void => {
        if (!ref.current) return;
        const delta = ref.current.startY - ev.clientY;
        const max = window.innerHeight * maxRatio;
        const next = Math.min(Math.max(ref.current.startH + delta, minH), max);
        setter((): number => next);
      };
      const onUp = (): void => {
        ref.current = null;
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        document.body.style.cursor = '';
      };
      document.body.style.cursor = 'row-resize';
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    };
  }

  const startDrag = useCallback(
    (e: React.MouseEvent): void =>
      makeDragger(dragState, setDockHeight, MIN_DOCK_H, MAX_DOCK_RATIO)(e),
    [],
  );

  const toggleCollapse = useCallback((): void => {
    setDockHeight((h): 280 | 36 => (h <= MIN_DOCK_H ? DEFAULT_DOCK_H : MIN_DOCK_H));
  }, []);

  async function togglePinAsTest(): Promise<void> {
    if (pinning) return;
    setPinning(true);
    setPinError(null);
    try {
      if (isPinned) {
        // We don't have the TC id in state — just optimistic toggle
        setIsPinned(false);
      } else {
        // Extract output from the vm.boot response event
        const bootResponse = events.find(
          (e): boolean => e.capability === 'vm' && e.action === 'boot' && e.phase === 'response',
        );
        const outputSnapshot = bootResponse?.payload ?? null;

        // Compute simple stable hashes for deduplication
        const inputHash = await stableHash(events);
        const outputHash = outputSnapshot !== null ? await stableHash(outputSnapshot) : null;

        const res = await fetch(`/api/projects/${projectId}/test-cases`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            executionId,
            entry,
            inputSnapshot: events,
            outputSnapshot,
            inputHash,
            outputHash,
          }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err?.error ?? `HTTP ${res.status}`);
        }
        setIsPinned(true);
      }
    } catch (err) {
      setPinError(err instanceof Error ? err.message : String(err));
    } finally {
      setPinning(false);
    }
  }

  /** Simple SHA-256 hash of a stable-stringified value, for deduplication. */
  async function stableHash(val: unknown): Promise<string> {
    const str = JSON.stringify(val, (_, v): unknown => {
      if (v === undefined) return null;
      if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
        return Object.fromEntries(
          Object.entries(v as Record<string, unknown>).sort(([a], [b]): number =>
            a.localeCompare(b),
          ),
        );
      }
      return v as unknown;
    });
    const bytes = new TextEncoder().encode(str);
    const buf = await crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(buf))
      .map((b): string => b.toString(16).padStart(2, '0'))
      .join('');
  }

  // ── Derive metadata ──────────────────────────────────────────────────────────
  // moved to context
  // moved to context
  // moved to context

  const bootPayload = bootEvent?.payload as Record<string, unknown> | null;
  const entry =
    (bootPayload?.['entry'] != null ? (bootPayload['entry'] as string) : null) ??
    (bootPayload?.['name'] != null ? (bootPayload['name'] as string) : null) ??
    executionId.slice(0, 8);
  const requestId =
    bootPayload?.['requestId'] != null ? (bootPayload['requestId'] as string) : undefined;
  const parentRequestId =
    (bootPayload?.['parentRequestId'] != null
      ? (bootPayload['parentRequestId'] as string)
      : null) ??
    (bootPayload?.['parentExecutionId'] != null
      ? (bootPayload['parentExecutionId'] as string)
      : null) ??
    undefined;

  // Fix "Invalid Date": use the real ISO receivedAt, not the relative `at` field
  const startedAt = formatTime(bootEvent?.receivedAt);

  const eventsWithPayload = events.filter(
    (e): boolean => e.payload !== null && e.payload !== undefined,
  );
  const allPayloadsProtected =
    eventsWithPayload.length > 0 && eventsWithPayload.every((e): boolean => isProtected(e.payload));
  const selectedEvent = selectedEventIdx !== null ? events[selectedEventIdx] : null;
  const insights = useMemo(
    () => deriveInsights(events, status, durationMs, errorEvent),
    [events, status, durationMs, errorEvent],
  );

  const fetchCount = networkCalls.length;
  const consoleCount = events.filter((e): boolean => e.capability === 'console').length;

  // ── Breadcrumb context from search params ────────────────────────────────────
  // Explorer passes ?from=explorer&fn=billing.charge so we can show breadcrumb + back link
  const fromExplorer = searchParams.get('from') === 'explorer';
  const fromFn = searchParams.get('fn');

  // ── Navigation ───────────────────────────────────────────────────────────────
  const selectEvent = useCallback(
    (idx: number): void => {
      setSelectedEventIdx(idx);
      setDrawerOpen(true);
      const ev = events[idx];
      if (ev?.capability === 'console') {
        setActivePanel('console');
        if (isCollapsed) setDockHeight(DEFAULT_DOCK_H);
      } else if (ev?.capability === 'fetch') {
        setActivePanel('network');
        if (isCollapsed) setDockHeight(DEFAULT_DOCK_H);
      }
    },
    [events, isCollapsed],
  );

  // Like selectEvent but never switches the active panel — used by Console to sync
  // the timeline selection without navigating away from the console tab.
  const selectEventOnly = useCallback(
    (idx: number): void => {
      setSelectedEventIdx(idx);
      setDrawerOpen(true);
      if (isCollapsed) setDockHeight(DEFAULT_DOCK_H);
    },
    [isCollapsed],
  );
  const stepUp = useCallback(
    (): void =>
      setSelectedEventIdx((p): number => {
        const n = p === null ? 0 : Math.max(0, p - 1);
        setDrawerOpen(true);
        return n;
      }),
    [],
  );
  const stepDown = useCallback(
    (): void =>
      setSelectedEventIdx((p): number => {
        const n = p === null ? 0 : Math.min(events.length - 1, p + 1);
        setDrawerOpen(true);
        return n;
      }),
    [events.length],
  );

  useEffect(() => {
    function handleKey(e: KeyboardEvent): void {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'ArrowUp' || e.key === 'k') {
        e.preventDefault();
        stepUp();
      }
      if (e.key === 'ArrowDown' || e.key === 'j') {
        e.preventDefault();
        stepDown();
      }
      if (e.key === 'Escape') setDrawerOpen(false);
    }
    window.addEventListener('keydown', handleKey);
    return (): void => window.removeEventListener('keydown', handleKey);
  }, [stepUp, stepDown]);

  return (
    <div
      className={cn(
        'relative flex flex-col bg-[#080a0f]',
        readOnlyStatic
          ? readOnlyStaticLayout === 'fill'
            ? 'h-full min-h-0 max-h-none w-full'
            : 'min-h-[28rem] h-[min(80vh,48rem)] max-h-[48rem] w-full'
          : 'h-[calc(100vh-3rem)]',
      )}
    >
      {/* ── Breadcrumb (from Explorer context) ──────────────────────────── */}
      {fromExplorer && !readOnlyStatic && (
        <div className="shrink-0 flex items-center gap-2 px-5 py-1.5 border-b border-white/5 bg-[#0a0c11]/60 text-[10px] font-mono text-muted-foreground/40">
          <Link
            to="/explorer"
            className="hover:text-muted-foreground transition-colors flex items-center gap-1"
          >
            <ArrowLeft className="h-3 w-3" /> Explorer
          </Link>
          <ChevronRight className="h-3 w-3" />
          {fromFn && <span className="text-muted-foreground/60">{fromFn}</span>}
          {fromFn && <ChevronRight className="h-3 w-3" />}
          <span className="text-foreground/50">{executionId.slice(0, 16)}…</span>
        </div>
      )}

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div
        className={cn(
          'shrink-0 px-5 py-3 border-b flex items-center gap-4',
          'bg-[#0a0c11]/95 backdrop-blur',
          isError ? 'border-[var(--flux-error)]/20' : 'border-white/8',
        )}
      >
        {/* Status badge */}
        <span
          className={cn(
            'px-2.5 py-1 rounded-[6px] text-[9px] font-mono tracking-widest uppercase border shrink-0',
            isError
              ? 'bg-[var(--flux-error)]/15 border-[var(--flux-error)]/30 text-[var(--flux-error)]'
              : 'bg-[var(--flux-success)]/15 border-[var(--flux-success)]/30 text-[var(--flux-success)]',
          )}
        >
          {status}
        </span>

        {/* Entry name */}
        <span className="text-sm font-semibold text-foreground truncate min-w-0 font-mono">
          {shortEntry(entry)}
        </span>

        {/* Duration */}
        <span
          className={cn(
            'font-mono text-sm font-bold shrink-0',
            durationMs > 1000
              ? 'text-[var(--flux-error)]'
              : durationMs > 400
                ? 'text-[var(--flux-warning)]'
                : 'text-[var(--flux-success)]',
          )}
        >
          {formatDuration(durationMs)}
        </span>

        <div className="h-4 w-px bg-white/10 shrink-0 mx-2" />

        {!readOnlyStatic && (
          <>
            <button
              onClick={(e): void => {
                const btn = e.currentTarget;
                void navigator.clipboard.writeText(window.location.href);
                const originalText = btn.innerHTML;
                btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-check text-emerald-400"><polyline points="20 6 9 17 4 12"/></svg> Copied`;
                setTimeout((): void => {
                  btn.innerHTML = originalText;
                }, 2000);
              }}
              className="flex items-center gap-1.5 px-3 py-1 rounded-md bg-blue-500/10 border border-blue-500/20 hover:bg-blue-500/20 transition-all text-[11px] font-mono font-medium text-blue-400 hover:text-blue-300"
            >
              <Share2 className="h-3 w-3" /> Share Trace
            </button>

            <button
              onClick={onToggleDataLock}
              title={
                isDataUnlocked ? 'Switch to raw locked view' : 'Switch to decrypted unlocked view'
              }
              className={cn(
                'flex items-center gap-1.5 px-3 py-1 rounded-md border transition-all text-[11px] font-mono font-medium',
                isDataUnlocked
                  ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20'
                  : 'bg-white/[0.04] border-white/10 text-muted-foreground/60 hover:text-foreground hover:bg-white/[0.08]',
              )}
            >
              {isDataUnlocked ? <Unlock className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
              {isDataUnlocked ? 'Lock Data' : 'Unlock Data'}
            </button>

            {isDataUnlocked && decrypting && (
              <span className="text-[10px] font-mono text-blue-300/80">Decrypting via agent…</span>
            )}
            {isDataUnlocked && decryptError && (
              <span
                className="text-[10px] font-mono text-yellow-300/80 max-w-56 truncate"
                title={decryptError}
              >
                decrypt failed: {decryptError}
              </span>
            )}
            {!isDataUnlocked && (
              <span className="text-[10px] font-mono text-muted-foreground/60">raw data mode</span>
            )}

            <button
              onClick={togglePinAsTest}
              disabled={pinning}
              title={isPinned ? 'Remove test case' : 'Pin as test case'}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1 rounded-md border transition-all text-[11px] font-mono font-medium disabled:opacity-50',
                isPinned
                  ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/25'
                  : 'bg-white/[0.04] border-white/10 text-muted-foreground/60 hover:text-foreground hover:bg-white/[0.08]',
              )}
            >
              <FlaskConical className="h-3 w-3" />
              {isPinned ? 'Test case' : 'Pin as test'}
            </button>
            {pinError && (
              <span
                className="text-[10px] text-red-400 font-mono truncate max-w-48"
                title={pinError}
              >
                ⚠ {pinError}
              </span>
            )}
          </>
        )}

        <div className="h-4 w-px bg-white/10 shrink-0 mx-1" />

        <CopyButton text={executionId} />
        {requestId && (
          <div className="hidden sm:flex items-center gap-1.5 text-[10px] font-mono text-muted-foreground/40">
            <Hash className="h-3 w-3" />
            <CopyButton text={requestId} label={requestId.slice(0, 16)} />
          </div>
        )}
        {parentRequestId && (
          <div className="hidden md:flex items-center gap-1.5 text-[10px] font-mono text-muted-foreground/30">
            <GitBranch className="h-3 w-3" />
            <span title={parentRequestId}>{parentRequestId.slice(0, 12)}</span>
          </div>
        )}

        <span className="flex-1" />

        {/* ── Timeline Scrubber ────────────────────────────────────────────────── */}
        <div
          className={cn(
            'flex items-center bg-[#0d1117] border border-white/10 rounded-lg p-1 shadow-inner',
            readOnlyStatic ? 'w-56' : 'w-64',
          )}
        >
          {!readOnlyStatic && (
            <button
              onClick={(): void => setReplayOpen(true)}
              className="flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-bold tracking-wide uppercase text-blue-400 hover:text-blue-300 hover:bg-white/[0.05] rounded transition-all shrink-0"
            >
              <Play className="h-3 w-3 fill-current" />
              Replay
            </button>
          )}

          <div className="flex-1 px-3 flex items-center relative">
            <div
              className="w-full h-1.5 bg-white/[0.05] rounded-full overflow-hidden flex relative group cursor-pointer"
              onClick={(e): void => {
                const rect = e.currentTarget.getBoundingClientRect();
                const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
                const targetIdx = Math.floor(pct * events.length);
                selectEvent(targetIdx);
              }}
            >
              <div
                className="absolute left-0 top-0 bottom-0 bg-blue-500/40 rounded-full transition-all duration-200"
                style={{
                  width: `${events.length > 0 ? ((selectedEventIdx ?? 0) / Math.max(1, events.length - 1)) * 100 : 0}%`,
                }}
              />
              <div
                className="absolute top-0 bottom-0 w-1 bg-blue-400 rounded-full shadow-[0_0_8px_rgba(96,165,250,0.8)] transition-all duration-200 -ml-0.5"
                style={{
                  left: `${events.length > 0 ? ((selectedEventIdx ?? 0) / Math.max(1, events.length - 1)) * 100 : 0}%`,
                }}
              />
            </div>
          </div>

          <div className="flex items-center gap-px">
            <button
              suppressHydrationWarning
              onClick={stepUp}
              className="p-1 text-muted-foreground/40 hover:text-foreground hover:bg-white/[0.06] rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={events.length === 0 || selectedEventIdx === null || selectedEventIdx <= 0}
            >
              <ChevronUp className="h-3 w-3" />
            </button>
            <button
              suppressHydrationWarning
              onClick={stepDown}
              className="p-1 text-muted-foreground/40 hover:text-foreground hover:bg-white/[0.06] rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={
                events.length === 0 ||
                selectedEventIdx === null ||
                selectedEventIdx >= events.length - 1
              }
            >
              <ChevronDown className="h-3 w-3" />
            </button>
          </div>
        </div>
      </div>

      {/* ── Narrative summary bar ─────────────────────────────────────────── */}
      <NarrativeBar />

      {/* ── Payload-protected banner ──────────────────────────────────────── */}
      {allPayloadsProtected && !bannerDismissed && !readOnlyStatic && (
        <div className="shrink-0 flex items-center gap-3 px-5 py-2.5 border-b border-yellow-500/20 bg-yellow-500/[0.04]">
          <Lock className="h-3.5 w-3.5 text-yellow-400/70 shrink-0" />
          <p className="text-[11px] text-muted-foreground/60 flex-1">
            {!isDataUnlocked && 'Data view is locked: showing raw encrypted payloads as recorded.'}
            {isDataUnlocked &&
              !canDecrypt &&
              'No agent URL configured for decrypt mode. Configure local or project agent URL.'}
            {isDataUnlocked &&
              canDecrypt &&
              decryptError &&
              `Unable to decrypt via agent: ${decryptError}`}
            {isDataUnlocked &&
              canDecrypt &&
              !decryptError &&
              'Payloads remain encrypted for this trace or by capture policy.'}
          </p>
          {!isDataUnlocked && (
            <button
              onClick={onToggleDataLock}
              className="text-[10px] font-mono rounded border border-yellow-500/30 bg-yellow-500/10 px-2 py-1 text-yellow-300 hover:bg-yellow-500/20 shrink-0"
            >
              Unlock
            </button>
          )}
          <button
            onClick={(): void => setBannerDismissed(true)}
            className="text-muted-foreground/40 hover:text-muted-foreground shrink-0"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* ── Stats + Insights strip ────────────────────────────────────────── */}
      <div className="shrink-0 px-5 py-2.5 border-b border-white/5 bg-[#0d1117]/60 flex items-start gap-8">
        <div className="flex items-center gap-8 shrink-0">
          <StatChip label="Started" value={startedAt} />
          <StatChip
            label="Duration"
            value={formatDuration(durationMs)}
            cls={durationMs > 1000 ? 'text-[var(--flux-error)]' : 'text-foreground'}
          />
          <StatChip label="Events" value={String(events.length)} />
          <StatChip
            label="Network"
            value={fetchCount > 0 ? `${fetchCount} call${fetchCount > 1 ? 's' : ''}` : 'none'}
            cls={fetchCount > 0 ? 'text-blue-400' : 'text-muted-foreground/40'}
          />
          <StatChip
            label="Logs"
            value={consoleCount > 0 ? `${consoleCount}` : '0'}
            cls={consoleCount > 0 ? 'text-foreground' : 'text-muted-foreground/40'}
          />
        </div>
        <div className="h-full w-px bg-white/5 self-stretch shrink-0" />
        <div className="flex-1 flex flex-col gap-1.5 min-w-0">
          {insights.map((ins, i): import('react/jsx-runtime').JSX.Element => (
            <InsightRow key={i} type={ins.type} text={ins.text} />
          ))}
        </div>
      </div>

      {/* ── Body: Timeline | Event Detail | Request Flow ───────────────────── */}
      <div className="flex overflow-hidden min-h-0" style={{ flex: '1 1 0' }}>
        {/* Left: Timeline Rail */}
        <div className="shrink-0 border-r border-white/5" data-testid="inspector-timeline-rail">
          <TimelineRail
            events={events}
            startAt={startAt}
            totalDurationMs={durationMs}
            selectedIdx={selectedEventIdx}
            onSelect={selectEvent}
            capabilityColors={CAPABILITY_COLORS}
          />
        </div>

        {/* Centre: Event Detail */}
        <div className="flex-1 min-h-0 overflow-auto">
          {drawerOpen && selectedEvent ? (
            <DetailDrawer
              event={selectedEvent}
              startAt={startAt}
              onClose={(): void => setDrawerOpen(false)}
              projectId={projectId}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center gap-3 text-muted-foreground/15 select-none">
              <List className="h-7 w-7" />
              <p className="text-sm">Select an event from the timeline</p>
              <p className="text-[11px] font-mono">↑ ↓ or J/K to navigate</p>
            </div>
          )}
        </div>

        {/* Right: Request Flow */}
        <div className="shrink-0 border-l border-white/5">
          {readOnlyStatic ? (
            <div className="w-56 p-4 text-center text-[11px] text-muted-foreground/60">
              Open the app to see the full request-flow graph and related runs.
            </div>
          ) : (
            <FlowPanel
              projectId={projectId}
              executionId={executionId}
              requestId={requestId}
              parentRequestId={parentRequestId}
              status={status as 'error' | 'success' | undefined}
            />
          )}
        </div>
      </div>

      {/* ── Full-width Debug Dock ──────────────────────────────────────────── */}
      <div
        onMouseDown={startDrag}
        onDoubleClick={toggleCollapse}
        className="shrink-0 h-[5px] flex items-center justify-center cursor-row-resize group bg-transparent hover:bg-white/[0.03] transition-colors border-t border-white/5"
        title="Drag to resize · Double-click to collapse"
      >
        <div className="w-10 h-[3px] rounded-full bg-white/10 group-hover:bg-white/25 transition-colors" />
      </div>

      <div
        className="shrink-0 bg-[#0a0c11]/80 backdrop-blur flex flex-col overflow-hidden"
        style={{ height: dockHeight }}
      >
        {/* Tab bar */}
        <div className="flex items-center justify-between px-1 border-b border-white/5 shrink-0">
          <div className="flex items-center">
            {TABS.map((tab): import('react/jsx-runtime').JSX.Element => (
              <button
                key={tab.id}
                onClick={(): void => {
                  setActivePanel(tab.id);
                  if (isCollapsed) setDockHeight(DEFAULT_DOCK_H);
                }}
                className={cn(
                  'flex items-center gap-1.5 px-3.5 py-2 text-[11px] font-mono transition-all border-b-2 -mb-px',
                  activePanel === tab.id && !isCollapsed
                    ? 'border-blue-400 text-foreground'
                    : 'border-transparent text-muted-foreground/40 hover:text-muted-foreground/70',
                )}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
          <button
            onClick={toggleCollapse}
            title={isCollapsed ? 'Expand' : 'Collapse'}
            className="mr-2 p-1.5 text-muted-foreground/30 hover:text-muted-foreground/70 transition-colors"
          >
            <CollapseIcon
              className={cn('h-3 w-3 transition-transform', isCollapsed ? 'rotate-180' : '')}
            />
          </button>
        </div>
        {!isCollapsed && (
          <div className="flex-1 overflow-auto min-h-0">
            {activePanel === 'console' && (
              <ConsolePanel onJumpToEvent={selectEventOnly} selectedIdx={selectedEventIdx} />
            )}
            {activePanel === 'network' && <NetworkPanel onSelect={selectEvent} />}
            {activePanel === 'performance' && (
              <PerformancePanel
                events={events}
                startAt={startAt}
                durationMs={durationMs}
                capabilityColors={CAPABILITY_COLORS}
                onSelect={selectEvent}
              />
            )}
            {activePanel === 'state' && <StatePanel events={events} startAt={startAt} />}
            {activePanel === 'events' && (
              <EventsPanel events={events} startAt={startAt} onSelect={selectEvent} />
            )}
          </div>
        )}
      </div>

      {!readOnlyStatic && (
        <ReplayDialog
          open={replayOpen}
          onOpenChange={setReplayOpen}
          projectId={projectId}
          executionId={executionId}
          productionUrl={agentUrl}
          originalDurationMs={durationMs}
        />
      )}
    </div>
  );
}
